import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { peopleDataFormatter } from "./SearchRedux";
import { searchQuery } from "./searchApi";
import MenuDialog from "../Shared/MenuDialog";
import AutocompleteMenu from "../Shared/AutocompleteMenu";

export function processPeopleResults(data) {
  const names = data.person_name ? data.person_name : [];
  const uniqnames = data.person_uniqname ? data.person_uniqname : [];
  const results = [...names, ...uniqnames];

  const uniqnameSet = new Set();
  const people = results.reduce((accumulator, person) => {
    if (!uniqnameSet.has(person.person_uniqname)) {
      accumulator.push(person);
      uniqnameSet.add(person.person_uniqname);
    }
    return accumulator;
  }, []);
  return peopleDataFormatter(people);
}

async function queryPeople(queryStr, idToken) {
  const response = await searchQuery("/supersearch/" + queryStr, idToken);
  const isValidResponse =
    !response.error &&
    response.data &&
    ((response.data.person_name && response.data.person_name.length) ||
      (response.data.person_uniqname && response.data.person_uniqname.length));

  if (!isValidResponse) {
    return [];
  }

  return processPeopleResults(response.data);
}

function PeopleAutocomplete({ open, selectHandler }) {
  const optionDisplay = {
    selected: ["person_name", "person_uniqname"],
    label: "person_name",
    labelId: (option) => option["person_uniqname"].toLowerCase(),
    labelContext: "rmrole",
  };

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const defaultData = useSelector(
    (state) => state.searchReducer.defaultData.people,
  );
  const idToken = useSelector((state) => state.authReducer.idToken);
  const formattedDefaultData =
    defaultData && Array.isArray(defaultData)
      ? defaultData.map((p) => {
          const person_name = p.person_name.replace(",", ", ");
          return {
            ...p,
            person_name,
          };
        })
      : [];

  useEffect(() => {
    setOptions(formattedDefaultData);
  }, [defaultData]);

  const latestRequestId = useRef(0);
  const onInputChange = async (input) => {
    const currentRequestId = ++latestRequestId.current;

    if (input.length < 2) {
      setOptions(formattedDefaultData || []);
      setLoading(false);
    } else {
      setLoading(true);
      const people = await queryPeople(input, idToken);

      // Handle out-of-order responses by only honoring the
      // response from the "most current" request.
      if (currentRequestId === latestRequestId.current) {
        const formattedNames = people.map((p) => {
          const person_name = p.person_name.replace(",", ", ");
          return {
            ...p,
            person_name,
          };
        });
        setLoading(false);
        setOptions(formattedNames);
      } else {
        console.log("Skipping out-of-order response for:", input);
      }
    }
  };

  return (
    <AutocompleteMenu
      open={open}
      loading={loading}
      selectHandler={selectHandler}
      inputLabel="People"
      optionDisplay={optionDisplay}
      options={options}
      onInputChange={onInputChange}
    />
  );
}

export default function PeopleMenu({
  visible,
  selectHandler,
  onCloseHandler,
  offset,
}) {
  return (
    <MenuDialog
      offset={offset}
      visible={visible}
      selectHandler={selectHandler}
      onCloseHandler={onCloseHandler}
      menuType="person"
    >
      <PeopleAutocomplete />
    </MenuDialog>
  );
}
